/**
 * Hashing and identifiers, built on Web Crypto (available in browsers and in Node ≥ 20).
 */

function bytes(input: string | Uint8Array): Uint8Array<ArrayBuffer> {
  if (typeof input === "string") return new TextEncoder().encode(input);
  const copy = new Uint8Array(input.byteLength);
  copy.set(input);
  return copy;
}

export async function sha256Hex(input: string | Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes(input));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Random UUID for records created in the app (idempotency key everywhere). */
export function newId(): string {
  return crypto.randomUUID();
}

/**
 * Deterministic UUID (RFC 9562 version 8) derived from a namespace and a key. Used for data that
 * must get the same id on every device, e.g. categories keyed by their exact Excel label, or rows
 * imported from the workbook — so sync packages and re-imports never create duplicates.
 */
export async function stableUuid(namespace: string, key: string): Promise<string> {
  const hex = (await sha256Hex(`${namespace}\u0000${key}`)).slice(0, 32).split("");
  hex[12] = "8";
  hex[16] = ((parseInt(hex[16]!, 16) & 0x3) | 0x8).toString(16);
  const h = hex.join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** JSON with object keys sorted recursively — a canonical form for checksums. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : v,
  );
}
